import { FC, memo, useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import ScrollableIcon from '/public/images/iqfu/public/scrollable_icon.svg';

import SectionLeadLeftAlign from '@components_iqfu/public/section_lead_left_align/SectionLeadLeftAlign';

import { CitiesLinkData } from '@type-def_iqfu/public/CitiesLinkData';
import { SalesStatsData } from '@type-def_iqfu/public/MarketData';

import { PublicPrefecturesWithAll } from '@constants_iqfu';
import { CompaniesPageQueryType } from 'src/iqfu/data/companiesPagesData';

import styles from './CitiesLinkList.module.scss';

type landHousesPricePageProps = {
  pageType: 'landhouses';
  citiesData: CitiesLinkData;
  prefectureId: number;
  landDisplayData: SalesStatsData;
  houseDisplayData: SalesStatsData;
  hasReadMoreButton?: boolean;
  location: string;
};

type mansionsPricePageProps = {
  pageType: 'mansions';
  citiesData: CitiesLinkData;
  prefectureId: number;
  mansionDisplayData: SalesStatsData;
  hasReadMoreButton?: boolean;
  location: string;
};

type companiesPageProps = {
  pageType: 'companies';
  citiesData: CitiesLinkData;
  prefectureId: number;
  pageQueryType: CompaniesPageQueryType;
  hasReadMoreButton?: boolean;
  location: string;
};

type Props = landHousesPricePageProps | mansionsPricePageProps | companiesPageProps;

const CitiesLinkList: FC<Props> = memo((props) => {
  const router = useRouter();
  const { pageType, citiesData, prefectureId, hasReadMoreButton = false, location } = props;
  const prefectureName = PublicPrefecturesWithAll[Number(prefectureId)];
  const [readMoreButtonClicked, setReadMoreButtonClicked] = useState(false);

  useEffect(() => {
    setReadMoreButtonClicked(false);
  }, [router]);

  return (
    <>
      {pageType === 'landhouses' || pageType === 'mansions' ? (
        <div className={styles.section_title}>
          <SectionLeadLeftAlign>
            市区町村別の{pageType === 'mansions' ? 'マンション' : '土地・中古戸建'}売却価格査定額の相場・動向を調べる
          </SectionLeadLeftAlign>
        </div>
      ) : (
        <>{/* 会社系ページはCompaniesPrefectuersCommon内に見出し書く */}</>
      )}

      <div
        className={`${styles.area_list_container} ${hasReadMoreButton ? styles.hasReadMoreButton : ''} ${
          readMoreButtonClicked ? styles.isScrollable : ''
        }`}
      >
        {citiesData.city_groups.map((cityList, index: number) => (
          <div className={styles.area_group} key={index}>
            <div className={styles.area_group_title}>
              {pageType === 'companies' ? (
                <h3>
                  <span className={styles.area_name}>
                    {prefectureName}-{cityList.city_group_name}
                  </span>
                  <br className={'sp_only'} />
                  の不動産屋さんランキングを見る
                </h3>
              ) : (
                <h3>
                  <span className={styles.area_name}>
                    {prefectureName}-{cityList.city_group_name}
                  </span>
                  <br className={'sp_only'} />の{pageType === 'mansions' ? 'マンション' : '土地戸建'}の売却査定額
                </h3>
              )}
              {pageType === 'mansions' ? (
                <AveragePrice
                  pageType={pageType}
                  groupCode={cityList.city_group_code}
                  mansionDisplayData={props.mansionDisplayData}
                />
              ) : pageType === 'landhouses' ? (
                <AveragePrice
                  pageType={pageType}
                  groupCode={cityList.city_group_code}
                  landDisplayData={props.landDisplayData}
                  houseDisplayData={props.houseDisplayData}
                />
              ) : (
                <></>
              )}
            </div>

            <ul className={styles.link_list}>
              {cityList.cities.map((city, i: number) => {
                return (
                  <li className={styles.link_item} key={i}>
                    <Link
                      prefetch={false}
                      href={
                        pageType === 'companies'
                          ? `/${pageType}/cities/${city.city_code}/${props.pageQueryType}`
                          : `/${pageType}/cities/${city.city_code}`
                      }
                      className={styles.areaNameLink}
                      data-gtm-click={`CitiesLinkList_item_city_links_${location}`}
                    >
                      {city.city_name}
                    </Link>
                  </li>
                );
              })}
            </ul>
            {hasReadMoreButton && (
              <div className={`${styles.read_more_container} ${readMoreButtonClicked ? styles.isClicked : ''}`}>
                <p className={styles.scrollable_text}>
                  <ScrollableIcon />
                  スクロール出来るようになります
                </p>
                <button
                  className={`${styles.read_more_button} ${readMoreButtonClicked ? styles.isClicked : ''}`}
                  onClick={() => setReadMoreButtonClicked(true)}
                >
                  もっと見る
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
});

CitiesLinkList.displayName = 'CitiesLinkList';

type AveragePriceProps = {
  groupCode: number;
  pageType: 'mansions' | 'landhouses' | 'companies';
  mansionDisplayData?: SalesStatsData;
  landDisplayData?: SalesStatsData;
  houseDisplayData?: SalesStatsData;
};

const AveragePrice: FC<AveragePriceProps> = memo(
  ({ groupCode, mansionDisplayData, landDisplayData, houseDisplayData, pageType }) => {
    const averageGroupData = mansionDisplayData?.city_groups_average_prices.find((group) => {
      return group.code === groupCode;
    });
    const houseAverageGroupData = houseDisplayData?.city_groups_average_prices.find((group) => {
      return group.code === groupCode;
    });
    const landAverageGroupData = landDisplayData?.city_groups_average_prices.find((group) => {
      return group.code === groupCode;
    });

    return (
      <div>
        {pageType === 'mansions' && averageGroupData?.average_price && (
          <div className={styles.average_mansion_price}>平均{averageGroupData?.average_price}万円/m2</div>
        )}

        {((pageType === 'landhouses' && landAverageGroupData?.average_price) ||
          (pageType === 'landhouses' && houseAverageGroupData?.average_price)) && (
          <div className={styles.landHouses_average_price_area}>
            {houseAverageGroupData?.average_price && <div>戸建：平均{houseAverageGroupData.average_price}万円/m2</div>}
            {landAverageGroupData?.average_price && <div>土地：平均{landAverageGroupData.average_price}万円/m2</div>}
          </div>
        )}
      </div>
    );
  },
);

AveragePrice.displayName = 'AveragePrice';

export default CitiesLinkList;
